import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight, BookOpen, Layers } from "lucide-react";
import { BASE_URL, blogsApi } from "../../../../api/api";
import api from "../../../../utils/api";
import { Link } from "react-router-dom";

const BlogPreview = () => {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await api.get(`${blogsApi}`);
                const data = response.data;
                setBlogPosts(data || []);
            } catch (error) {
                console.error("Error fetching blogs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    const activePosts = blogPosts.filter(
        (post) => post.status === 1 || post.status === true,
    );

    // Clean, professional fade-up animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200">
            <div className="max-w-7xl mx-auto">
                {/* Structural Editorial Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-gray-200 pb-6">
                    <div className="max-w-2xl">
                        <h2 className="text-4xl font-black text-neutral-900 mb-2">
                            Blog &{" "}
                            <span className="text-yellow-500">
                                Current Affairs.
                            </span>
                        </h2>
                        <p className="text-gray-600 text-base font-medium">
                            Expert tips, proven strategies, and crucial daily
                            updates tailored specifically for your target
                            examinations.
                        </p>
                    </div>

                    {/* View All Button positioned efficiently in the header */}
                    {!loading && activePosts.length > 3 && (
                        <Link
                            to="/blogs"
                            className="hidden md:inline-flex items-center px-6 py-2.5 bg-neutral-900 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                        >
                            View All Articles
                            <ArrowRight
                                size={16}
                                className="ml-2 group-hover:translate-x-1 transition-transform"
                            />
                        </Link>
                    )}
                </div>

                {/* Loading Skeleton */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col animate-pulse"
                            >
                                <div className="w-full aspect-video bg-gray-200"></div>
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="h-6 bg-gray-200 rounded w-full mb-3"></div>
                                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                                    <div className="h-4 bg-gray-100 rounded w-5/6 mb-6"></div>
                                    <div className="mt-auto h-10 bg-gray-200 rounded-lg w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : activePosts.length > 0 ? (
                    /* Professional Editorial Grid */
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {activePosts.slice(0, 3).map((post) => (
                            <motion.div
                                key={post.id}
                                variants={cardVariants}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col group overflow-hidden"
                            >
                                {/* Image Container with Professional Date Stamp */}
                                <div className="w-full aspect-video relative overflow-hidden bg-gray-100 border-b border-gray-100">
                                    <img
                                        src={
                                            post.image ||
                                            "https://i.pinimg.com/1200x/34/50/b0/3450b07a0875803c03d792fadb8a6a84.jpg"
                                        }
                                        alt={post.title}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.style.objectFit =
                                                "contain";
                                            e.target.style.backgroundColor =
                                                "#f0f0f0";
                                            e.target.style.filter =
                                                "grayscale(100%)";
                                            e.target.style.opacity = "0.5";
                                            e.target.src = "/assets/logo.png";
                                        }}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                    />
                                    <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/10 transition-colors duration-300"></div>

                                    {/* Structural Date Badge */}
                                    <div className="absolute top-4 right-4 bg-white px-3 py-1.5 rounded flex items-center text-neutral-900 shadow-sm border border-gray-200">
                                        <CalendarDays
                                            size={14}
                                            className="text-yellow-500 mr-2"
                                        />
                                        <span className="text-xs font-bold">
                                            {new Date(
                                                post.created_at,
                                            ).toLocaleDateString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                            })}
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 flex flex-col flex-1">
                                    <h3 className="text-xl font-black text-neutral-900 mb-3 leading-snug line-clamp-2 group-hover:text-red-700 transition-colors">
                                        {post.title}
                                    </h3>

                                    <p className="text-gray-600 text-sm font-medium leading-relaxed mb-6 line-clamp-3 flex-1">
                                        {post.excerpt ||
                                            "Click to read more about this topic and stay updated with the latest insights and analysis."}
                                    </p>

                                    {/* Action Button */}
                                    <div className="mt-auto pt-4 border-t border-gray-100">
                                        <Link
                                            to={`/blog-details-component/${post.id}`}
                                            className="w-full flex items-center justify-between px-4 py-2.5 bg-neutral-900 text-white group-hover:bg-yellow-400 group-hover:text-neutral-900 rounded-lg font-bold text-sm transition-colors duration-300"
                                        >
                                            <div className="flex items-center">
                                                <BookOpen
                                                    size={16}
                                                    className="mr-2 opacity-80"
                                                />
                                                <span>Read Full Article</span>
                                            </div>
                                            <ArrowRight
                                                size={16}
                                                className="transform group-hover:translate-x-1 transition-transform"
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm w-full">
                        <BookOpen
                            size={32}
                            className="mx-auto text-gray-400 mb-3"
                        />
                        <p className="text-neutral-900 font-bold text-base mb-1">
                            No articles available.
                        </p>
                        <p className="text-gray-500 font-medium text-sm">
                            Check back soon for the latest updates and
                            strategies.
                        </p>
                    </div>
                )}

                {/* Mobile View All Button (Hidden on Desktop) */}
                {!loading && activePosts.length > 3 && (
                    <div className="mt-8 flex justify-center md:hidden">
                        <Link
                            to="/blogs"
                            className="inline-flex items-center w-full justify-center px-6 py-3 bg-neutral-900 text-white font-bold rounded-lg shadow-sm"
                        >
                            View All Articles
                            <ArrowRight size={16} className="ml-2" />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};

export default BlogPreview;
